/* eslint-disable react-hooks/exhaustive-deps */
import {
  AddIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckIcon,
  DeleteIcon,
  Search2Icon,
} from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useGlobalFilter, usePagination, useTable } from "react-table";

export default function AddNewCodeForm({ onSubmit, formRef }) {
  const [filterInput, setFilterInput] = useState("");
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: { codeId: "", description: "", options: [] },
    mode: "onChange",
  });

  const [editIndex, setEditIndex] = useState(null);
  const data = watch("options");
  const hoverBg = useColorModeValue("gray.100", "gray.700");
  const columns = useMemo(
    () => [
      {
        Header: "Text",
        accessor: "TEXT",
        Cell: ({ row, value }) =>
          editIndex === row.index ? (
            <Input
              {...register(`options.${row.index}.TEXT`, { required: true })}
              placeholder="Text"
              size="sm"
            />
          ) : (
            <Text>{value}</Text>
          ),
      },
      {
        Header: "Code",
        accessor: "CODE",
        Cell: ({ row, value }) =>
          editIndex === row.index ? (
            <Input
              {...register(`options.${row.index}.CODE`, { required: true })}
              placeholder="Code"
              size="sm"
            />
          ) : (
            <Text>{value}</Text>
          ),
      },
      {
        Header: "Subgroup",
        accessor: "SUBGROUP",
        Cell: ({ row, value }) =>
          editIndex === row.index ? (
            <Input
              {...register(`options.${row.index}.SUBGROUP`)}
              placeholder="Subgroup"
              size="sm"
            />
          ) : (
            <Text color={value ? "black" : "gray.400"}>{value || "—"}</Text>
          ),
      },

      {
        Header: "Actions",
        Cell: ({ row }) =>
          editIndex === row.index ? (
            <Flex gap={4} alignItems="start" justify="start">
              <CheckIcon
                onClick={(e) => {
                  e.stopPropagation();
                  setEditIndex(null);
                }}
                cursor="pointer"
                _hover={{ color: "green" }}
                fontSize={15}
              />
            </Flex>
          ) : (
            <Flex justify="start" gap={5}>
              <DeleteIcon
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteRow(row.index);
                }}
                fontSize="15px"
                cursor="pointer"
                color="red"
              />
            </Flex>
          ),
      },
    ],
    [editIndex, register, data]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setGlobalFilter,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 10 },
    },
    useGlobalFilter,
    usePagination
  );

  const handleAddRow = () => {
    const newRow = { TEXT: "", CODE: "", SUBGROUP: "" };
    const updated = [newRow, ...data]; // Add to top
    setValue("options", updated);
    setEditIndex(0); // Focus on the top (new) row
  };

  const handleDeleteRow = (index) => {
    const updated = [...data];
    updated.splice(index, 1);
    setValue("options", updated);
    setEditIndex(null);
  };

  useEffect(() => {
    if (formRef) {
      formRef.current = {
        submit: handleSubmit((values) => {
          onSubmit(values);
        }),
      };
    }
  }, [formRef, handleSubmit, onSubmit]);

  const handleSearchChange = (e) => {
    const value = e.target.value || undefined;
    setGlobalFilter(value);
    setFilterInput(value);
  };

  return (
    <Box>
      <form>
        <Box mb={3}>
          <Box mb={2}>
            <Text fontWeight={600} fontSize="md">
              Code ID
            </Text>
            <Input
              {...register("codeId", { required: "Code ID is required" })}
              placeholder="Enter Code ID"
              size="sm"
              w="350px"
              onChange={(e) => {
                setValue("codeId", e.target.value.toUpperCase());
                trigger("codeId");
              }}
            />
            {errors.codeId && (
              <Text fontSize="xs" color="red.500" mt={1}>
                {errors.codeId.message}
              </Text>
            )}
          </Box>
          <Box>
            <Text fontWeight={600} fontSize="md">
              Add Description
            </Text>
            <Textarea
              {...register("description")}
              rows={2}
              placeholder="Enter description"
              size="sm"
            />
          </Box>
        </Box>

        <Flex mb={1} justify="space-between" align="center">
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Search2Icon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search codes..."
              value={filterInput}
              onChange={handleSearchChange}
              width="300px"
              borderRadius="md"
              size="sm"
            />
          </InputGroup>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="teal"
            onClick={handleAddRow}
            size="sm"
          >
            Add New
          </Button>
        </Flex>

        <TableContainer
          maxH="50vh"
          overflowY="auto"
          border="1px solid gray"
          borderRadius="lg"
        >
          <Table {...getTableProps()} variant="simple" size="sm">
            <Thead position="sticky" top={0} zIndex={1} bg="gray.50">
              {headerGroups.map((headerGroup) => (
                <Tr {...headerGroup.getHeaderGroupProps()} textAlign="center">
                  {headerGroup.headers.map((column) => (
                    <Th {...column.getHeaderProps()}>
                      {column.render("Header")}
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <Tr
                    {...row.getRowProps()}
                    _hover={{ bg: hoverBg }}
                    cursor="pointer"
                    onClick={() => setEditIndex(row.index)}
                  >
                    {row.cells.map((cell) => (
                      <Td {...cell.getCellProps()}>{cell.render("Cell")}</Td>
                    ))}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>

        <Flex justify="start" align="center" mt={1}>
          <Box display="flex" gap={5} alignItems="center">
            <ArrowLeftIcon
              onClick={() => previousPage()}
              cursor={canPreviousPage ? "pointer" : "not-allowed"}
              color={canPreviousPage ? "black" : "gray.400"}
            />
            <Text fontSize="sm" color="gray.600">
              Page {pageIndex + 1} of {pageOptions.length}
            </Text>
            <ArrowRightIcon
              onClick={() => nextPage()}
              cursor={canNextPage ? "pointer" : "not-allowed"}
              color={canNextPage ? "black" : "gray.400"}
            />
          </Box>
        </Flex>
      </form>
    </Box>
  );
}
